// node_modules
import { FC, useContext, useEffect, useState } from "react"
// Custom hooks
import { useFetch, usePagination } from "Hooks";
// Types
import { TUseFetch, TUsPatentDTO } from "Types";
// Components
import { AsynchronousWrapper, Pagination, UsPatentDocument } from "Components";
// Logics
import { BookmarkLogicSingleton } from "Logics";
// Contexts
import { CaseContext } from "Stores";
import { PendoHelperSingleton } from "Helpers";
import { LoggingConstants } from "Constants";

type TDecoupledPatentDocumentsProps = {
    ids: string[],
    onHighlight?: (documentId: string) => void,
    handlePatentHasBeenUnbookmarked?: (unbookmarkOnEntityId: string) => Promise<void>,
    handlePatentHasBeenBookmarked?: (bookmarkOnEntityId: string) => Promise<void>,
    amountPerPage?: number
}

export const DecoupledPatentDocuments: FC<TDecoupledPatentDocumentsProps> = ({ ids, onHighlight, handlePatentHasBeenUnbookmarked, handlePatentHasBeenBookmarked, amountPerPage }: TDecoupledPatentDocumentsProps) => {
    // Contexts
    const { caseState } = useContext(CaseContext);

    const {currentPage, amountOfPages, paginateToNextPage, paginateToPreviousPage} = usePagination(amountPerPage, ids.length);
    
    const [url, setUrl] = useState("");  
    
    useEffect(() => {
        let newUrl = "api/articles/patents?";

        let idsToUse: string[];
        if(!amountPerPage) {
            idsToUse = ids;
        } else {
            idsToUse = ids.slice((currentPage - 1) * amountPerPage, (currentPage ) * amountPerPage);
        }
            // Build url by adding id array query param
        idsToUse.forEach((id: string, index: number) => {
            newUrl += `ids[]=${id}`
            if (index + 1 < idsToUse.length) newUrl += "&";
        });

        if (caseState.selectedCaseId) {
            newUrl += `&caseId=${caseState.selectedCaseId}`;
        }
        setUrl(newUrl);
    }, [caseState.selectedCaseId, ids, currentPage, amountPerPage]);

    // Logging
    useEffect(() => {
        PendoHelperSingleton.trackEvent(`${LoggingConstants.DYNAMIC_DELIVERY}ArticlesGetDecoupledPatents`);
    }, [url]);

    // We use `useFetch` custom hook here
    // to fetch data from the server
    const { isLoading, didError, errorMessage, fetchedData }: TUseFetch<TUsPatentDTO[]> = useFetch(url, [currentPage]);

    return (
        <AsynchronousWrapper didError={didError} errorMessage={errorMessage} isLoading={isLoading} LoaderComponent={null}>
            {!fetchedData ? <h3>Loading</h3>
                :
                <div style={{ padding: "1rem 2rem" }}>
                    {
                        fetchedData.map((patentDto, index) => {
                            patentDto.isBookmarked = BookmarkLogicSingleton.isResultBookmarked(patentDto.coworkerBookmarkOn, patentDto.scoutBookmarkOn);
                            return (
                                <UsPatentDocument 
                                    key={patentDto.id + "-" + index} 
                                    patentDto={patentDto} 
                                    onClick={onHighlight} 
                                    handlePatentHasBeenUnbookmarked={handlePatentHasBeenUnbookmarked} 
                                    handlePatentHasBeenBookmarked={handlePatentHasBeenBookmarked} />
                            );
                        })
                    }
                    {amountPerPage && amountOfPages > 1 ? <Pagination currentPagePosition={currentPage} maximumPagePosition={amountOfPages} goToPreviousPage={paginateToPreviousPage} goToNextPage={paginateToNextPage} /> : null}
                </div>
            }
        </AsynchronousWrapper>
    );
}